/**
 * Support access in the app (#23; the server side is backend/services/support.js). On the Leads page,
 * Timefly staff pick a company, a person and a reason, and the app becomes that person's view, read
 * only, for 30 minutes. A banner on every page says so, and Avslutt goes back to the staff member's
 * own session. Analytics is off for the whole session (Shell).
 */
import { get, post } from '@/api/client';
import { SUPPORT_RETURN, type SessionUser, supportReturn, useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { commitGeneration } from '@/shell/context';
import { Button, Panel, toast } from '@/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Company {
  id: number;
  name: string;
  is_demo: boolean;
}
interface Person {
  id: number;
  name: string;
  lastname: string | null;
  role: string;
}
interface Started {
  token: string;
  user: SessionUser;
  support: { session: number; company: string; expires_at: string };
}

/** On the Leads page: nothing at all for someone the server does not count as staff. */
export function SupportPanel() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [company, setCompany] = useState<Company | null>(null);
  const [person, setPerson] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const companies = useQuery({
    queryKey: ['support', 'companies', q],
    queryFn: () => get<Company[]>(`/support/companies?q=${encodeURIComponent(q)}`),
    retry: false,
  });
  const people = useQuery({
    queryKey: ['support', 'people', company?.id],
    queryFn: () => get<Person[]>(`/support/people?tenant_id=${company?.id}`),
    enabled: !!company,
    retry: false,
  });
  // not staff: the server said no; an answer that is not a list is no list either
  if (!Array.isArray(companies.data)) return null;
  const peopleList = Array.isArray(people.data) ? people.data : [];

  async function start() {
    if (!company || !person) return;
    setBusy(true);
    try {
      const r = await post<Started>('/support/start', { tenant_id: company.id, user_id: person, reason });
      const { token, user } = useAuth.getState();
      if (token && user) sessionStorage.setItem(SUPPORT_RETURN, JSON.stringify({ token, user }));
      useAuth.getState().setSession(r.token, { ...r.user, support: r.support });
      commitGeneration(r.user.id, company.id, company.name);
      nav('/', { replace: true });
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={T('Se som bruker')} sub={T('support: kun lesing, 30 minutter, alt logges')}>
      <div style={{ display: 'grid', gap: 8 }} data-testid="support-panel">
        <input
          className="inp"
          aria-label={T('Finn bedrift')}
          placeholder={T('Finn bedrift')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setCompany(null);
            setPerson(null);
          }}
        />
        {!company ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {companies.data.slice(0, 12).map((c) => (
              <Button small key={c.id} onClick={() => setCompany(c)}>
                {c.name}
                {c.is_demo ? `, ${T('demo')}` : ''}
              </Button>
            ))}
          </div>
        ) : (
          <>
            <b>{company.name}</b>
            <select
              className="inp"
              aria-label={T('Person')}
              value={person ?? ''}
              onChange={(e) => setPerson(Number(e.target.value) || null)}
            >
              <option value="">{T('Velg person')}</option>
              {peopleList.map((p) => (
                <option key={p.id} value={p.id}>
                  {`${p.name} ${p.lastname ?? ''}`.trim()} ({p.role})
                </option>
              ))}
            </select>
            <textarea
              className="inp"
              aria-label={T('Hvorfor')}
              placeholder={T('Hvorfor ser du på kontoen? For eksempel: Hjem krasjer for kunden.')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button tone="pri" disabled={busy || !person || reason.trim().length < 5} onClick={start}>
                {T('Se som denne personen')}
              </Button>
              <Button onClick={() => setCompany(null)}>{T('Annen bedrift')}</Button>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

/** On every page during a support session. */
export function SupportBanner() {
  const nav = useNavigate();
  const user = useAuth((s) => s.user);
  if (!user?.support) return null;
  const until = new Date(user.support.expires_at).toLocaleTimeString('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  });

  async function end() {
    try {
      await post('/support/end', {});
    } catch {
      // ended or expired already: going back is the same
    }
    const back = supportReturn();
    sessionStorage.removeItem(SUPPORT_RETURN);
    if (back) {
      useAuth.getState().setSession(back.token, back.user);
      commitGeneration(back.user.id, null, null);
      nav('/leads', { replace: true });
    } else {
      useAuth.getState().clear();
      nav('/login', { replace: true });
    }
  }

  return (
    <output className="supportbar" data-testid="support-banner">
      <span>
        {T('Du ser Timefly som {name} i {company}. Support, kun lesing, til {until}.', {
          name: `${user.name} ${user.lastname ?? ''}`.trim(),
          company: user.support.company,
          until,
        })}
      </span>
      <Button small onClick={end}>
        {T('Avslutt')}
      </Button>
    </output>
  );
}
